// "What stands out" for the facilities page: plain-language findings about which restrooms are
// busiest, when the rush comes and which restrooms are coming up on a cleaning, ranked so the most
// useful ones come first. Same card format as the concessions and gates insights.
import React from 'react';
import {
  CLEANING_THRESHOLD, DUE_SOON_AT, combinedVisitSeries, visitFlow, peakQuarter, clockToMinute, minuteToClock, formatDueIn, QUEUE_TARGET,
} from './facilitiesMetrics';
import { formatDuration } from '../Concession/live/liveMetrics';

const pct = (part, whole) => (whole ? Math.round((part / whole) * 100) : 0);
const durationText = (m) => (m >= 60 ? formatDuration(m) : `${m} min`);
const rangeText = (a, b) => (b < 60 ? `${a}–${b} min` : `${durationText(a)} – ${durationText(b)}`);
const span = (p) => `${minuteToClock(p.start)} – ${minuteToClock(p.end)}`;

// "Section 114 Men's", "A and B", "A, B and C"
export const listNames = (names) => (names.length <= 1 ? names[0] || '' : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`);

// A quarter-hour relative to game start, as a range: "35–50 min before game start"
const relativeToStart = (slot, gameStart) => {
  if (gameStart == null) return null;
  const from = slot.start - gameStart;
  const to = slot.end - gameStart;
  if (from < 0 && to > 0) return 'around game start';
  if (to <= 0) {
    const near = Math.abs(to);
    const far = Math.abs(from);
    return near === 0 ? 'in the 15 minutes before game start' : `${rangeText(near, far)} before game start`;
  }
  return from === 0 ? 'in the first 15 minutes after game start' : `${rangeText(from, to)} after game start`;
};

// stats: buildRestroomStats results (for the window, when one is picked)
export const buildFacilitiesInsights = ({ stats, range, currentGame, isLive }) => {
  if (!stats.length) return [];
  const insights = [];
  const gameStart = clockToMinute(currentGame?.gameStartTime);
  const total = stats.reduce((sum, s) => sum + s.visits, 0);
  const multi = stats.length > 1;

  // Live: restrooms coming up on a cleaning, soonest first
  if (isLive) {
    const soon = stats
      .filter((s) => s.cycle.dueSoon)
      .sort((a, b) => a.cycle.toGo - b.cycle.toGo);
    if (soon.length) {
      const top = soon.slice(0, 3);
      insights.push({
        key: 'soon',
        tone: 'warn',
        icon: 'stars',
        score: 110,
        label: 'Due for cleaning soon',
        text: (
          <>
            <strong>{soon.length}</strong> restroom{soon.length === 1 ? ' is' : 's are'} within {CLEANING_THRESHOLD - DUE_SOON_AT} entries of a cleaning:
            {' '}{top.map((s, i) => (
              <React.Fragment key={s.id}>
                {i ? (i === top.length - 1 ? ' and ' : ', ') : ''}
                <strong>{s.label}</strong> ({s.cycle.toGo} to go{s.dueIn != null ? `, ${formatDueIn(s.dueIn)}` : ''})
              </React.Fragment>
            ))}
            {soon.length > top.length ? `, plus ${soon.length - top.length} more` : ''}.
          </>
        ),
      });
    } else if (total > 0) {
      insights.push({
        key: 'soon',
        tone: 'good',
        icon: 'check-circle',
        score: 30,
        label: 'Cleaning',
        text: <>No restroom is within {CLEANING_THRESHOLD - DUE_SOON_AT} entries of its next cleaning.</>,
      });
    }
  }

  // Longest queues: the restroom with the most time at or over the target (one reading a minute)
  const withQueue = stats.filter((s) => s.queuePeak);
  if (withQueue.length) {
    const worst = [...withQueue].sort((a, b) => b.longMinutes - a.longMinutes || b.queuePeak.value - a.queuePeak.value)[0];
    const isLong = worst.longMinutes > 0;
    insights.push({
      key: 'queues',
      tone: isLong ? 'bad' : 'good',
      icon: 'hourglass-split',
      score: isLong ? 100 : 40,
      label: isLong ? 'Longest queues' : 'Queues',
      text: isLong ? (
        <>
          <strong>{worst.label}</strong> had the longest queues: at {QUEUE_TARGET} min or more for
          {' '}<strong>{formatDuration(worst.longMinutes)}</strong>{range ? ' in this window' : ''}, peaking at
          {' '}<strong>{worst.queuePeak.value} min</strong> at {minuteToClock(worst.queuePeak.minute)}.
        </>
      ) : (
        <>Queues stayed short: every restroom stayed under {QUEUE_TARGET} min{range ? ' in this window' : ''}.</>
      ),
    });
  }

  // Cleanings due: how many, and where
  const dueTotal = stats.reduce((sum, s) => sum + s.dueCount, 0);
  if (total > 0) {
    const most = [...stats].sort((a, b) => b.dueCount - a.dueCount || b.visits - a.visits);
    const topDue = most[0];
    const tied = most.filter((s) => s.dueCount === topDue.dueCount);
    insights.push({
      key: 'due',
      tone: dueTotal ? 'warn' : 'good',
      icon: 'clipboard-check',
      score: isLive ? 75 : 95,
      label: 'Cleanings due',
      text: dueTotal ? (
        <>
          <strong>{dueTotal}</strong> cleaning{dueTotal === 1 ? '' : 's'} due {isLive ? 'so far' : range ? 'in this window' : 'this game'} (one every {CLEANING_THRESHOLD} entries)
          {multi && topDue.dueCount > 0 && (
            tied.length > 1 && tied.length <= 3
              ? <>; <strong>{listNames(tied.map((s) => s.label))}</strong> needed the most, {topDue.dueCount} each</>
              : tied.length === 1 ? <>; most at <strong>{topDue.label}</strong> ({topDue.dueCount})</> : ''
          )}.
        </>
      ) : (
        <>No restroom reached {CLEANING_THRESHOLD} entries{range ? ' in this window' : ''}, so no cleanings were due.</>
      ),
    });
  }

  // When the rush comes: busiest quarter-hour across every restroom
  const combined = combinedVisitSeries(stats.map((s) => s.series));
  const flow = visitFlow(combined, 1).filter((b) => !range || (b.start >= range.start && b.start < range.end));
  const rush = peakQuarter(flow);
  if (rush) {
    const when = relativeToStart(rush, gameStart);
    insights.push({
      key: 'rush',
      tone: 'info',
      icon: 'graph-up-arrow',
      score: 90,
      label: 'Busiest time',
      text: (
        <>
          Restrooms were busiest <strong>{span(rush)}</strong>{when ? <> ({when})</> : ''}: <strong>{rush.total.toLocaleString()}</strong> entries
          {total ? <>, {pct(rush.total, total)}% of the {range ? 'window' : isLive ? 'day so far' : 'game'}</> : ''}.
        </>
      ),
    });
  }

  // Busiest restroom
  if (multi && total > 0) {
    const sorted = [...stats].sort((a, b) => b.visits - a.visits);
    const top = sorted[0];
    const tied = sorted.filter((s) => s.visits === top.visits);
    insights.push({
      key: 'busiest',
      tone: 'info',
      icon: 'person-walking',
      score: 80,
      label: 'Busiest restroom',
      text: tied.length > 1 ? (
        <><strong>{listNames(tied.map((s) => s.label))}</strong> were the busiest, with <strong>{top.visits.toLocaleString()}</strong> entries each.</>
      ) : (
        <>
          <strong>{top.label}</strong> was the busiest, with <strong>{top.visits.toLocaleString()}</strong> entries
          {' '}({pct(top.visits, total)}% of all){top.peak ? <>, peaking {span(top.peak)}</> : ''}.
        </>
      ),
    });
  }

  // Men's vs women's
  const byType = {};
  stats.forEach((s) => { byType[s.restroom.type] = (byType[s.restroom.type] || 0) + s.visits; });
  if (byType.mens != null && byType.womens != null && byType.mens + byType.womens > 0) {
    const womensShare = pct(byType.womens, byType.mens + byType.womens);
    const lead = womensShare >= 50 ? { name: "Women's", share: womensShare } : { name: "Men's", share: 100 - womensShare };
    insights.push({
      key: 'type',
      tone: 'info',
      icon: 'people-fill',
      score: 55,
      label: "Men's and women's",
      text: Math.abs(womensShare - 50) <= 3 ? (
        <>Men&apos;s and women&apos;s restrooms were used about evenly (<strong>{byType.mens.toLocaleString()}</strong> and <strong>{byType.womens.toLocaleString()}</strong> entries).</>
      ) : (
        <>
          <strong>{lead.name}</strong> restrooms took <strong>{lead.share}%</strong> of men&apos;s and women&apos;s entries
          {' '}({byType.womens.toLocaleString()} women&apos;s, {byType.mens.toLocaleString()} men&apos;s).
        </>
      ),
    });
  }

  // Uneven load across sections
  const bySection = new Map();
  stats.forEach((s) => bySection.set(s.restroom.sectionId, (bySection.get(s.restroom.sectionId) || 0) + s.visits));
  const sections = [...bySection.entries()].sort((a, b) => b[1] - a[1]);
  if (sections.length >= 3) {
    const [topId, topVisits] = sections[0];
    const [lowId, lowVisits] = sections[sections.length - 1];
    if (lowVisits > 0 && topVisits / lowVisits >= 1.8) {
      insights.push({
        key: 'uneven',
        tone: 'info',
        icon: 'distribute-vertical',
        score: 65,
        label: 'Uneven use',
        text: (
          <>
            <strong>Section {topId}</strong> had <strong>{(topVisits / lowVisits).toFixed(1)}×</strong> the entries of
            {' '}<strong>Section {lowId}</strong> ({topVisits.toLocaleString()} vs {lowVisits.toLocaleString()}).
          </>
        ),
      });
    }
  }

  return insights.sort((a, b) => b.score - a.score).slice(0, 6);
};
